/**
 * Tiroir latéral : liste des traces chargées.
 *
 * Reprend `drawer_content.xml` et `item_gpx_track.xml`. Le réordonnancement
 * se fait uniquement par la poignée : dans l'application Android, le
 * glisser-déposer sur toute la carte a été désactivé parce qu'il déclenchait
 * des déplacements involontaires en roulant.
 */

import { createIcon, iconMarkup } from './icons'
import { showDialog } from './dialogs'
import type { GpxTrack } from '../core/track'
import type { Translator } from '../i18n'
import {
  isFullscreen,
  isFullscreenSupported,
  onFullscreenChange,
  setFullscreen,
} from '../core/fullscreen'
import {
  installMode,
  onInstallAvailabilityChange,
  promptInstall,
} from '../core/install'

/**
 * Déplacement en deçà duquel le geste ne réordonne rien.
 *
 * Sans ce seuil, un appui tremblant sur la poignée changerait l'ordre de la
 * liste sans que rien n'ait été voulu.
 */
const DRAG_THRESHOLD_PX = 6

export interface DrawerCallbacks {
  onOpenFile(): void
  onOpenSettings(): void
  onSelectTrack(trackId: string): void
  onTrackMenu(trackId: string, anchor: HTMLElement): void
  onColorTap(trackId: string, anchor: HTMLElement): void
  onReorder(trackIds: readonly string[]): void
  /** Appui sur l'avancement d'un calcul d'itinéraire : l'interrompre. */
  onCancelRouting(trackId: string): void
}

export class Drawer {
  readonly element: HTMLElement
  readonly #scrim: HTMLElement
  readonly #list: HTMLElement
  readonly #empty: HTMLElement
  readonly #fullscreenAction: HTMLButtonElement
  readonly #installAction: HTMLButtonElement

  #tracks: readonly GpxTrack[] = []

  /** Abandon du déplacement en cours, s'il y en a un. */
  #endDrag: (() => void) | null = null

  constructor(
    container: HTMLElement,
    private readonly t: Translator,
    private readonly callbacks: DrawerCallbacks,
  ) {
    this.#scrim = document.createElement('div')
    this.#scrim.className = 'scrim'
    this.#scrim.dataset.open = 'false'
    this.#scrim.addEventListener('click', () => this.close())

    this.element = document.createElement('nav')
    this.element.className = 'drawer'
    this.element.dataset.open = 'false'
    this.element.setAttribute('aria-label', t.t('track_list'))

    const header = document.createElement('div')
    header.className = 'drawer__header'

    const title = document.createElement('h1')
    title.className = 'drawer__title'
    title.textContent = t.t('app_name')

    // Le numéro de version reste dans les réglages, section « à propos » : il
    // n'a rien à faire en tête du tiroir, où seul le nom situe l'application.
    const identity = document.createElement('div')
    identity.className = 'drawer__identity'
    identity.append(title)

    // Deux commandes rondes en tête du tiroir, chacune masquée là où elle
    // n'aurait aucun effet — plutôt qu'un créneau unique partagé, qui obligeait
    // à choisir laquelle des deux perdre sur un appareil qui accepte les deux.
    //
    // Le plein écran se prend et se rend là : c'est le geste du départ et de
    // l'arrivée, il ne doit pas se chercher dans les réglages. Il manque sur
    // iPhone, où Safari ne l'accorde qu'aux vidéos, et sur une page en cadre
    // sans `allow`.
    //
    // L'installation, elle, dépend du navigateur bien plus que de l'appareil :
    // `core/install.ts` en tient le compte, et le bouton n'existe que si
    // quelque chose peut réellement se produire à l'appui.
    this.#fullscreenAction = document.createElement('button')
    this.#fullscreenAction.type = 'button'
    this.#fullscreenAction.className = 'drawer__header-action'
    // Ce que la commande fait, posé sur l'élément : le libellé change de langue
    // et d'état, il ne peut pas servir de prise à une sonde.
    this.#fullscreenAction.dataset.action = 'fullscreen'
    this.#fullscreenAction.hidden = !isFullscreenSupported()
    this.#fullscreenAction.addEventListener('click', () => {
      void setFullscreen(!isFullscreen())
      // Agir depuis le tiroir le referme, comme le choix d'une trace : la
      // commande n'a d'intérêt que pour revenir à la carte.
      this.close()
    })
    this.#syncFullscreen()

    // Échap et le geste du navigateur sortent du plein écran sans passer par
    // le bouton. Le tiroir vit aussi longtemps que l'application :
    // l'abonnement n'a pas à être résilié.
    onFullscreenChange(() => this.#syncFullscreen())

    this.#installAction = document.createElement('button')
    this.#installAction.type = 'button'
    this.#installAction.className = 'drawer__header-action'
    this.#installAction.dataset.action = 'install'
    this.#installAction.addEventListener('click', () => {
      this.close()

      if (installMode() === 'guide') {
        this.#showInstallHelp()
        return
      }

      void promptInstall().then(() => this.#syncInstall())
    })
    this.#syncInstall()

    // `beforeinstallprompt` part quand le navigateur le décide, souvent après
    // la construction du tiroir ; et l'utilisateur peut aussi installer depuis
    // le menu du navigateur, ce qui doit retirer la commande. Lire le mode une
    // seule fois laisserait donc un bouton absent quand il faut, présent quand
    // il ne faut plus.
    onInstallAvailabilityChange(() => this.#syncInstall())

    const headerActions = document.createElement('div')
    headerActions.className = 'drawer__header-actions'
    headerActions.append(this.#installAction, this.#fullscreenAction)

    header.append(identity, headerActions)

    const openButton = document.createElement('button')
    openButton.type = 'button'
    openButton.className = 'button-tonal drawer__open-button'
    openButton.innerHTML = iconMarkup('folderOpen', 20)
    openButton.append(document.createTextNode(t.t('open_gpx')))
    openButton.addEventListener('click', callbacks.onOpenFile)

    const listTitle = document.createElement('h2')
    listTitle.className = 'drawer__section-title'
    listTitle.textContent = t.t('track_list')

    this.#list = document.createElement('div')
    this.#list.className = 'drawer__list'

    this.#empty = document.createElement('p')
    this.#empty.className = 'drawer__empty'
    this.#empty.textContent = t.t('no_tracks_loaded')

    const footer = document.createElement('div')
    footer.className = 'drawer__footer'

    const settingsButton = document.createElement('button')
    settingsButton.type = 'button'
    settingsButton.className = 'button-tonal'
    settingsButton.innerHTML = iconMarkup('settings', 20)
    settingsButton.append(document.createTextNode(t.t('settings')))
    settingsButton.addEventListener('click', callbacks.onOpenSettings)

    footer.append(settingsButton)

    this.element.append(header, openButton, listTitle, this.#list, footer)
    container.append(this.#scrim, this.element)

    this.setTracks([])
  }

  get isOpen(): boolean {
    return this.element.dataset.open === 'true'
  }

  open(): void {
    this.element.dataset.open = 'true'
    this.#scrim.dataset.open = 'true'
  }

  close(): void {
    this.element.dataset.open = 'false'
    this.#scrim.dataset.open = 'false'
  }

  toggle(): void {
    if (this.isOpen) this.close()
    else this.open()
  }

  /** Icône et libellé du bouton, alignés sur l'état réel du document. */
  #syncFullscreen(): void {
    const active = isFullscreen()
    const label = this.t.t(active ? 'exit_fullscreen' : 'fullscreen')

    this.#fullscreenAction.title = label
    this.#fullscreenAction.setAttribute('aria-label', label)
    this.#fullscreenAction.replaceChildren(
      createIcon(active ? 'fullscreenExit' : 'fullscreen'),
    )
  }

  /**
   * Icône, libellé et présence du bouton d'installation.
   *
   * Deux dessins, parce que les deux gestes ne mènent pas au même endroit :
   * `prompt` ouvre la boîte du navigateur — un appui, et l'application est
   * posée —, tandis que `guide` n'affiche qu'un mode d'emploi vers la feuille
   * de partage de Safari. Le glyphe de partage sur ce second cas est délibéré :
   * c'est exactement le bouton que l'œil devra retrouver dans Safari.
   */
  #syncInstall(): void {
    const mode = installMode()

    this.#installAction.hidden = mode === 'none'
    if (mode === 'none') return

    const label = this.t.t(mode === 'prompt' ? 'install_app' : 'add_to_home_screen')

    this.#installAction.title = label
    this.#installAction.setAttribute('aria-label', label)
    this.#installAction.replaceChildren(
      createIcon(mode === 'prompt' ? 'installApp' : 'iosShare'),
    )
  }

  /**
   * Marche à suivre pour poser l'application sur l'écran d'accueil.
   *
   * Aucun navigateur d'iOS n'expose de demande d'installation déclenchable —
   * il n'y a pas d'équivalent de `beforeinstallprompt`. La seule voie passe
   * par la feuille de partage du système : elle se décrit, elle ne s'ouvre
   * pas. D'où les étapes littérales, et le glyphe de partage sur le bouton
   * plutôt qu'une icône d'installation, pour que l'œil retrouve dans Safari
   * ce qu'il vient de voir ici.
   */
  #showInstallHelp(): void {
    const body = document.createElement('div')

    const intro = document.createElement('p')
    intro.className = 'install-help__intro'
    intro.textContent = this.t.t('add_to_home_screen_intro')

    const steps = document.createElement('ol')
    steps.className = 'install-help__steps'

    for (const key of [
      'add_to_home_screen_step_share',
      'add_to_home_screen_step_add',
      'add_to_home_screen_step_open',
    ] as const) {
      const step = document.createElement('li')
      step.textContent = this.t.t(key)
      steps.append(step)
    }

    body.append(intro, steps)

    showDialog(document.body, {
      title: this.t.t('add_to_home_screen'),
      body,
      actions: [{ label: this.t.t('ok'), primary: true }],
    })
  }

  setTracks(tracks: readonly GpxTrack[]): void {
    // La liste est reconstruite de zéro : un déplacement en cours perdrait sa
    // ligne sans rien pour le signaler — une récupération d'altitude qui
    // rafraîchit sa progression suffit à provoquer ce cas.
    this.#endDrag?.()

    this.#tracks = tracks

    if (tracks.length === 0) {
      this.#list.replaceChildren(this.#empty)
      return
    }

    this.#list.replaceChildren(...tracks.map((track) => this.#buildCard(track)))
  }

  #buildCard(track: GpxTrack): HTMLElement {
    const card = document.createElement('div')
    card.className = 'track-card'
    card.dataset.trackId = track.id
    card.dataset.visible = String(track.visible)

    const handle = document.createElement('span')
    handle.className = 'track-card__handle'
    handle.title = this.t.t('reorder_track')
    handle.append(createIcon('dragHandle'))
    this.#wireDragHandle(handle, card, track.id)

    const color = document.createElement('button')
    color.type = 'button'
    color.className = 'track-card__color'
    color.style.background = track.color
    color.title = this.t.t('change_color')
    color.setAttribute('aria-label', this.t.t('change_color'))
    color.addEventListener('click', (event) => {
      event.stopPropagation()
      this.callbacks.onColorTap(track.id, color)
    })

    const body = document.createElement('div')
    body.className = 'track-card__body'

    const nameRow = document.createElement('div')
    nameRow.className = 'track-card__name-row'

    const name = document.createElement('div')
    name.className = 'track-card__name'
    name.textContent = track.name
    nameRow.append(name)

    /*
     * Une trace routée le dit, comme sur l'APK.
     *
     * Le tiroir est le seul endroit où Android le signale — ni la fiche
     * d'infos ni les réglages ne portent l'icône, ces derniers se contentant de
     * basculer le libellé de leur entrée. C'est une **description
     * d'accessibilité seule** : aucun libellé visible, aucune info-bulle, et
     * rien de cliquable, la ligne entière ouvrant déjà la trace.
     */
    if (track.isRouted) {
      const routed = createIcon('route', 18)
      routed.classList.add('track-card__routed')
      routed.setAttribute('aria-hidden', 'false')
      routed.setAttribute('role', 'img')
      routed.setAttribute('aria-label', this.t.t('routed_track'))
      nameRow.append(routed)
    }

    const info = document.createElement('div')
    info.className = 'track-card__info'
    info.textContent = this.#formatTrackInfo(track)

    body.append(nameRow, info)

    // Tâche en cours : l'avancement remplace les informations habituelles, qui
    // ne changeront de toute façon qu'une fois le calcul terminé.
    if (track.taskProgress !== null) {
      // Un calcul d'itinéraire s'annule d'un appui, une récupération
      // d'altitude non : c'est le partage d'Android, dont le spinner de
      // routage porte un écouteur et celui d'altitude aucun. La balise suit
      // donc la fonction — un bouton quand le geste existe, sans quoi un
      // lecteur d'écran annoncerait une commande qui n'en est pas une.
      const progress = document.createElement(track.isRouting ? 'button' : 'div')
      progress.className = 'track-card__task'

      if (progress instanceof HTMLButtonElement) {
        progress.type = 'button'
        progress.title = this.t.t('cancel_routing')
        progress.setAttribute('aria-label', this.t.t('cancel_routing'))
        progress.addEventListener('click', (event) => {
          // Sans cela, l'appui traverserait jusqu'à la carte, qui cadre la
          // trace et referme le tiroir : l'annulation partirait bien, mais
          // l'utilisateur ne verrait que le tiroir se fermer.
          event.stopPropagation()
          this.callbacks.onCancelRouting(track.id)
        })
      }

      const bar = document.createElement('span')
      bar.className = 'track-card__task-bar'
      bar.style.width = `${Math.round(track.taskProgress * 100)}%`

      const label = document.createElement('span')
      label.className = 'track-card__task-label'
      label.textContent = `${track.taskLabel ?? ''} ${Math.round(track.taskProgress * 100)} %`.trim()

      progress.append(bar, label)
      body.append(progress)
    }

    const more = document.createElement('button')
    more.type = 'button'
    more.className = 'track-card__more'
    more.title = this.t.t('track_settings')
    more.setAttribute('aria-label', this.t.t('track_settings'))
    more.append(createIcon('moreVert'))
    more.addEventListener('click', (event) => {
      event.stopPropagation()
      this.callbacks.onTrackMenu(track.id, more)
    })

    // Un appui sur la carte ferme le tiroir et cadre la trace : c'est le
    // geste le plus fréquent, il ne doit demander qu'un seul contact.
    card.addEventListener('click', () => {
      this.callbacks.onSelectTrack(track.id)
      this.close()
    })

    card.append(handle, color, body, more)
    return card
  }

  /**
   * Les deux ou trois lignes d'une carte de trace.
   *
   * Quand un départ réel est configuré, `GpxListAdapter.kt` annonce sa
   * position, puis répète distance et D+ **recalculés depuis lui** : le
   * coureur lit ce qu'il lui reste, sans soustraire de tête une approche qui
   * ne compte pas. Le format de la seconde mesure est celui de la première,
   * comme côté Android, pour que les deux se comparent d'un coup d'œil.
   */
  #formatTrackInfo(track: GpxTrack): string {
    const measure = (meters: number, gain: number): string =>
      track.hasElevationData
        ? this.t.t('track_info_format', {
            distance: this.t.number(meters / 1000, 1),
            gain: Math.round(gain),
          })
        : this.t.t('track_info_format_no_ele', {
            distance: this.t.number(meters / 1000, 1),
          })

    const lines = [measure(track.totalDistance, track.elevationGain)]

    if (track.hasRealStart) {
      lines.push(
        this.t.t('real_start_at', {
          // Deux décimales là où les distances en portent une : le départ réel
          // est saisi au mètre près et sert de repère à retrouver sur la
          // trace, pas d'ordre de grandeur.
          value: this.t.number(track.realStartDistance / 1000, 2),
        }),
        measure(
          track.distanceFromRealStart,
          track.gainLossFromDistance(track.realStartDistance).gain,
        ),
      )
    }

    return lines.join('\n')
  }

  /**
   * Rend une ligne déplaçable par sa poignée.
   *
   * Les événements de pointeur remplacent le glisser-déposer HTML5, dont
   * l'implémentation mobile est inexistante : au doigt, aucun `dragstart`
   * n'était jamais émis, et la fonction restait hors d'atteinte sur la
   * plateforme principale du projet.
   *
   * Le déplacement se fait par la poignée et non par un appui long sur la
   * ligne, comme sur Android : la ligne entière reste ainsi cliquable, et le
   * geste ne peut pas être confondu avec un appui distrait.
   */
  #wireDragHandle(handle: HTMLElement, card: HTMLElement, trackId: string): void {
    handle.addEventListener('pointerdown', (event) => {
      // Le clic droit et les gestes à plusieurs doigts ne déplacent rien.
      if (event.button !== 0) return

      event.preventDefault()
      this.#beginDrag(card, trackId, event.clientY, event.pointerId)
    })
  }

  /**
   * Suit le doigt jusqu'au relâchement.
   *
   * L'ordre est recalculé à chaque déplacement et appliqué au DOM sur-le-champ
   * : voir les lignes s'écarter est ce qui rend le geste compréhensible, une
   * simple bordure de destination laisse deviner.
   *
   * Les événements sont écoutés sur la fenêtre et non sur la poignée, et la
   * capture de pointeur est délibérément écartée : réordonner la liste déplace
   * la poignée dans le document, ce qui relâche sa capture au milieu du geste.
   * La ligne restait alors figée sous le doigt.
   */
  #beginDrag(
    card: HTMLElement,
    trackId: string,
    startY: number,
    pointerId: number,
  ): void {
    if (this.#endDrag !== null) return

    let order = this.#tracks.map((track) => track.id)
    let moved = false

    card.classList.add('track-card--dragging')

    const onMove = (event: PointerEvent): void => {
      if (event.pointerId !== pointerId) return

      const offset = event.clientY - startY
      card.style.transform = `translateY(${offset}px)`

      // Seuil de quelques pixels : sans lui, un appui tremblant réordonnerait
      // la liste sans que l'utilisateur ait rien voulu déplacer.
      if (!moved && Math.abs(offset) < DRAG_THRESHOLD_PX) return
      moved = true

      const target = this.#dropIndex(card)
      const from = order.indexOf(trackId)
      if (target === -1 || from === -1 || target === from) return

      order = [...order]
      order.splice(target, 0, ...order.splice(from, 1))

      // La ligne déplacée garde sa position à l'écran : elle suit le doigt, ce
      // sont les autres qui bougent autour d'elle. L'origine du geste est donc
      // recalée du décalage que la permutation vient de lui imposer.
      const before = card.getBoundingClientRect().top
      this.#applyOrder(order)
      const after = card.getBoundingClientRect().top
      startY += after - before
      card.style.transform = `translateY(${event.clientY - startY}px)`
    }

    const finish = (commit: boolean): void => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
      this.#endDrag = null

      card.classList.remove('track-card--dragging')
      card.style.transform = ''

      if (commit && moved) this.callbacks.onReorder(order)
    }

    const onUp = (event: PointerEvent): void => {
      if (event.pointerId === pointerId) finish(true)
    }

    const onCancel = (event: PointerEvent): void => {
      if (event.pointerId === pointerId) finish(false)
    }

    // Retenu pour pouvoir abandonner le geste si la liste est reconstruite
    // sous lui : la ligne tirée disparaîtrait alors sans que rien ne le dise.
    this.#endDrag = () => finish(false)

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
  }

  /**
   * Rang final de la ligne déplacée dans l'ordre des traces.
   *
   * Le rang est cherché **parmi les autres lignes**, la ligne tirée exclue :
   * sa position dans le rang courant décalerait d'un cran toute insertion vers
   * le bas. Comparé au **milieu** de chaque voisine plutôt qu'à son bord, ce
   * qui donne une bascule franche au passage, sans allers-retours quand le
   * doigt s'arrête sur une frontière.
   */
  #dropIndex(dragged: HTMLElement): number {
    const others = ([...this.#list.children] as HTMLElement[]).filter(
      (element) => element !== dragged,
    )

    // Position visuelle réelle, transformation comprise : la reconstruire à
    // partir de l'origine du geste dérive dès la première permutation.
    const box = dragged.getBoundingClientRect()
    const center = box.top + box.height / 2

    for (const [index, other] of others.entries()) {
      const rect = other.getBoundingClientRect()
      if (center < rect.top + rect.height / 2) return index
    }

    return others.length
  }

  /** Réordonne les lignes affichées sans les reconstruire. */
  #applyOrder(order: readonly string[]): void {
    const byId = new Map<string, HTMLElement>()
    for (const element of this.#list.children) {
      const id = (element as HTMLElement).dataset.trackId
      if (id) byId.set(id, element as HTMLElement)
    }

    // `append` déplace un élément déjà présent plutôt que de le recréer : la
    // ligne tirée conserve son état, y compris la capture du pointeur.
    for (const id of order) {
      const element = byId.get(id)
      if (element) this.#list.append(element)
    }
  }
}
